/**
 * Утилиты для работы с датой и временем
 */

/**
 * Получить текущую дату и время в Luxembourg timezone (Europe/Luxembourg)
 * @returns {string} Formatted date and time string
 *
 * @example
 * getCurrentDateTime()
 * // Returns: "Tuesday, November 4, 2025 at 15:30 CET (Central European Time)"
 */
export function getCurrentDateTime() {
  const now = new Date();

  // Форматируем полную дату с днем недели
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Luxembourg',
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Форматируем время в 24-часовом формате
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Luxembourg',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  const formattedDate = dateFormatter.format(now);
  const formattedTime = timeFormatter.format(now);

  // Определяем текущий offset для CET/CEST
  const isDST = isDaylightSavingTime(now);
  const timezone = isDST ? 'CEST' : 'CET';
  const timezoneFull = isDST ? 'Central European Summer Time' : 'Central European Time';

  return `${formattedDate} at ${formattedTime} ${timezone} (${timezoneFull})`;
}

/**
 * Проверяет, действует ли летнее время (DST) в Европе/Люксембурге
 * DST в Европе: с последнего воскресенья марта до последнего воскресенья октября
 * @param {Date} date - Дата для проверки
 * @returns {boolean} true если летнее время, false если стандартное
 */
function isDaylightSavingTime(date) {
  const year = date.getFullYear();

  // Найти последнее воскресенье марта
  const marchLastSunday = getLastSundayOfMonth(year, 2); // March = 2

  // Найти последнее воскресенье октября
  const octoberLastSunday = getLastSundayOfMonth(year, 9); // October = 9

  // DST начинается в 01:00 UTC последнего воскресенья марта
  const dstStart = new Date(Date.UTC(year, 2, marchLastSunday, 1, 0, 0));

  // DST заканчивается в 01:00 UTC последнего воскресенья октября
  const dstEnd = new Date(Date.UTC(year, 9, octoberLastSunday, 1, 0, 0));

  const timestamp = date.getTime();
  return timestamp >= dstStart.getTime() && timestamp < dstEnd.getTime();
}

/**
 * Получить день последнего воскресенья в указанном месяце
 * @param {number} year - Год
 * @param {number} month - Месяц (0-11)
 * @returns {number} День месяца
 */
function getLastSundayOfMonth(year, month) {
  // Начинаем с последнего дня месяца
  const lastDay = new Date(year, month + 1, 0);

  // Идем назад пока не найдем воскресенье (день недели = 0)
  while (lastDay.getDay() !== 0) {
    lastDay.setDate(lastDay.getDate() - 1);
  }

  return lastDay.getDate();
}

/**
 * Получить текущую дату в формате ISO (YYYY-MM-DD) в Luxembourg timezone
 * @returns {string} ISO date string
 *
 * @example
 * getCurrentDateISO()
 * // Returns: "2025-11-04"
 */
export function getCurrentDateISO() {
  const now = new Date();

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Luxembourg',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  return formatter.format(now); // Returns YYYY-MM-DD
}

/**
 * Получить текущее время в формате HH:MM в Luxembourg timezone
 * @returns {string} Time string in HH:MM format
 *
 * @example
 * getCurrentTimeHHMM()
 * // Returns: "15:30"
 */
export function getCurrentTimeHHMM() {
  const now = new Date();

  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Luxembourg',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

  return formatter.format(now);
}
